import { Waves } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, InfoCard } from "../components/Section";
import Reveal from "../components/Reveal";
import { LOCATIONS } from "../lib/content";
import { useHours } from "../hooks/useHours";

/** The marina's own published lake information, carried over verbatim. No
 *  acreage, horsepower limit, distance, ramp detail, date or regulation here is
 *  inferred. The park maps are the official ODNR sheets, self-hosted. */
const WEST_BRANCH = {
  id: "west-branch",
  title: "West Branch State Park and Michael J Kirwan Resevoir",
  intro: [
    "West Branch State Park is located inside Portage county in Ravenna Ohio. Reachable from both Cleveland and Pittsburgh areas",
    "The 2,650-acre lake is designated for unlimited horsepower. A no-wake zone extends 300 feet from all shorelines. The entire area west of Rock Spring Road causeway is also a no-wake zone",
  ],
  rampsLead: "Five launch ramps provide access to the lake; one is in the campground:",
  ramps: [
    {
      name: "East Boat Ramp:",
      body: "Two paved lanes, ample parking, and lighting. A restroom with flush toilets is also available. Courtesy docks are available during boating season.",
    },
    {
      name: "West Boat Ramp:",
      body: "Two paved lanes, ample parking, lighting, and a life jacket loaner board. A unisex vault latrine is available. Courtesy docks are available during boating season.",
    },
    {
      name: "Camp Ramp:",
      body: "Two paved lanes, ample parking (including several spots designated for registered campers only), lighting, and a vault latrine at the south end of the ramp. The camp store has a lifejacket loaner station inside.",
    },
    {
      name: "Rock Spring Road Ramp:",
      body: "A gravel ramp is located on the north end of the lake off of Rock Spring Road. A unisex non-flush restroom is available as well as ample parking.",
    },
    {
      name: "Knapp Road Ramp:",
      body: "A primitive gravel ramp is located at the west end of the lake.",
    },
  ],
  notes: [
    "There are two boat swimming areas: Silver Creek Bay (southwest of the beach) and Jay Lake (across from the Camp Ramp). Both have buoys marking the area.",
  ],
  map: "/site-assets/lake-information/west-branch-map.png",
  mapAlt: "Official Ohio Department of Natural Resources map of West Branch State Park",
};

const LAKE_MILTON = {
  id: "lake-milton",
  title: "Lake Milton State Park",
  intro: [
    "Nearly 1,700 acres of some of the best recreational waters in Ohio can be found at Lake Milton State Park, located in northeast Ohio. Boating, swimming and fishing are popular at this day-use park.",
  ],
  rampsLead: "There are three launch ramps:",
  ramps: [
    {
      name: "Miller’s Landing:",
      body: "Two paved lanes, parking, lighting. a lifejacket loaner station, and flush restrooms are available.",
    },
    {
      name: "Robinson Point:",
      body: "Two paved lanes, ample parking, lighting, and flush restrooms are available.",
    },
    {
      name: "Pointview:",
      body: "Two paved lanes, ample parking. and lighting. A vault latrine is available",
    },
  ],
  notes: [
    "There are three boat swim areas: NW corner of the Mahoning Ave Bridge, the Island, and an area east of the Pointview Boat Ramp. All areas are marked with a boat swim buoy.",
    "There are courtesy docks at the north end of the beach along with a boat pump out station. Courtesy docks are also located on the island and near the gazebo off Mahoning Ave.",
    "Fall draw down typically begins October 15th and we start the repooling process on March 15th (contingent upon the weather conditions). Lake Milton drops approximately 8 feet in the winter.",
  ],
  map: "/site-assets/lake-information/lake-milton-map.png",
  mapAlt: "Official Ohio Department of Natural Resources map of Lake Milton State Park",
};

const LakeBlock = ({ lake, tone }) => (
  <Section tone={tone}>
    <SectionHeading eyebrow="Lake information" title={lake.title} />

    <div data-testid={`lake-${lake.id}`} className="mt-10 grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
      <Reveal>
        {lake.intro.map((p) => (
          <p key={p} className="mb-4 text-base leading-relaxed text-slate-700">
            {p}
          </p>
        ))}

        <h3 className="mt-8 font-display text-lg font-bold text-navy">{lake.rampsLead}</h3>
        <ul data-testid={`lake-${lake.id}-ramps`} className="mt-5 space-y-4">
          {lake.ramps.map((r) => (
            <li key={r.name} className="rounded-2xl border border-sand-border bg-sand p-5">
              <span className="font-display text-sm font-bold text-navy">{r.name}</span>{" "}
              <span className="text-sm leading-relaxed text-slate-700">{r.body}</span>
            </li>
          ))}
        </ul>

        {lake.notes.map((n) => (
          <p key={n} className="mt-5 text-sm leading-relaxed text-slate-700">
            {n}
          </p>
        ))}
      </Reveal>

      <Reveal delay={90}>
        <a
          href={lake.map}
          target="_blank"
          rel="noreferrer"
          data-testid={`lake-${lake.id}-map-link`}
          className="block overflow-hidden rounded-3xl border border-sand-border bg-white transition-transform hover:-translate-y-1"
        >
          <img
            src={lake.map}
            alt={lake.mapAlt}
            data-testid={`lake-${lake.id}-map-image`}
            className="w-full object-contain"
            loading="lazy"
          />
        </a>
      </Reveal>
    </div>
  </Section>
);

export default function LakeInformation() {
  const { hoursFor } = useHours();

  return (
    <>
      <Seo
        title="Lake Information & Launch Ramps | West Branch State Park"
        description="West Branch State Park has five launch ramps, with courtesy docks at the East and West ramps. Plan your launch and your day on the lake."
        path="/lake-information"
        image="/images/lake_aerial.jpeg"
      />

      <PageHero
        image="/images/lake_aerial.jpeg"
        alt="Aerial view of the lake and wooded shoreline at West Branch State Park"
        eyebrow="Lake information"
        title="Know the water before you go"
        subtitle="Ramps, courtesy docks and the practical details of launching at West Branch State Park."
      />

      <LakeBlock lake={WEST_BRANCH} tone="white" />
      <LakeBlock lake={LAKE_MILTON} />

      <Section tone="white">
        <SectionHeading
          eyebrow="On the day"
          title="Marina hours"
          intro="These hours come straight from our booking system, so they match what you can actually reserve."
        />
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {Object.values(LOCATIONS).map((loc) => {
            const h = hoursFor(loc.slug);
            return (
              <InfoCard key={loc.slug} icon={Waves} title={loc.park} testId={`lake-hours-${loc.slug}`}>
                <span className="block font-semibold text-navy">
                  {h ? `Daily ${h.open} – ${h.close}` : "Call for this season's hours."}
                </span>
                <a
                  href={loc.phoneHref}
                  data-testid={`lake-hours-phone-${loc.slug}`}
                  className="mt-2 inline-block font-bold text-lake hover:underline"
                >
                  {loc.phone}
                </a>
              </InfoCard>
            );
          })}
        </div>
      </Section>
    </>
  );
}
